import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'

const DOCUMENT_CATEGORIES = [
    { key: 'barangay_clearance', label: 'Barangay Business Clearance' },
    { key: 'mayors_permit', label: "Mayor's / Business Permit" },
    { key: 'sanitary_permit', label: 'Sanitary Permit & Health Certificates' },
    { key: 'fire_safety', label: 'Fire Safety Inspection Certificate' },
    { key: 'bir_registration', label: 'BIR Registration' },
]

// Branch admins can't set 'inactive' themselves — that's an HQ-level archive action
const STATUS_OPTIONS = [
    { value: 'operating', label: 'Operating' },
    { value: 'closed', label: 'Closed' },
    { value: 'maintenance', label: 'Maintenance' },
]

const getStatusColor = (status) => {
    switch (status) {
        case 'operating': return 'bg-green-100 text-green-800'
        case 'closed': return 'bg-gray-100 text-gray-800'
        case 'maintenance': return 'bg-yellow-100 text-yellow-800'
        case 'inactive': return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
    }
}

export default function AdminBranchManagement({ staffData, onGoToProfile }) {
    const branchId = staffData?.branch_id

    const [branch, setBranch] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [editing, setEditing] = useState(false)

    const [formData, setFormData] = useState(null)
    const [initialFormData, setInitialFormData] = useState(null)

    const [documents, setDocuments] = useState([])
    const [loadingDocs, setLoadingDocs] = useState(false)
    const [newDocFilesByCategory, setNewDocFilesByCategory] = useState({})

    useEffect(() => {
        if (branchId) {
            fetchBranch()
            fetchDocuments()
        }
    }, [branchId])

    const toFormData = (b) => ({
        name: b.name || '',
        address: b.address || '',
        open_time: b.open_time ? b.open_time.slice(0, 5) : '',
        close_time: b.close_time ? b.close_time.slice(0, 5) : '',
        operating_start_date: b.operating_start_date || '',
        status: b.status || 'operating',
        branch_owner: b.branch_owner || '',
        manager: b.manager || '',
        owner_is_manager: !!b.branch_owner && b.branch_owner === b.manager,   // ← add this line
        tin: b.tin || '',
        facebook_url: b.facebook_url || '',
        phone_number: b.phone_number || '',
        telephone_number: b.telephone_number || '',
        email: b.email || '',
        business_email: b.business_email || '',
        eod_report_time: b.eod_report_time ? b.eod_report_time.slice(0, 5) : '',
    })

    const fetchBranch = async () => {
        try {
            setLoading(true)
            console.log('branchId being queried:', branchId)

            const { data, error } = await supabase
                .from('branches')
                .select('*')
                .eq('branch_id', branchId)
                .maybeSingle()

            console.log('branch query result:', JSON.stringify({ data, error }))
            
            const { data: { session } } = await supabase.auth.getSession()
            console.log('session:', session)
            console.log('user id:', session?.user?.id)
            console.log('user email:', session?.user?.email)
            
            if (error) throw error

            if (!data) {
                setBranch(null)
                setFormData(null)
                setInitialFormData(null)
                return
            }

            setBranch(data)
            const normalized = toFormData(data)
            setFormData(normalized)
            setInitialFormData(normalized)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to load branch', text: err.message })
        } finally {
            setLoading(false)
        }
    }

    const fetchDocuments = async () => {
        try {
            setLoadingDocs(true)
            const { data, error } = await supabase
                .from('branch_documents')
                .select('*')
                .eq('branch_id', branchId)
                .order('uploaded_at', { ascending: false })
            if (error) throw error
            setDocuments(data || [])
        } catch (err) {
            console.error('Error fetching branch documents:', err)
        } finally {
            setLoadingDocs(false)
        }
    }

    const hasChanges =
        JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
        Object.values(newDocFilesByCategory).some(f => (f || []).length > 0)

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.address.trim() || !formData.open_time || !formData.close_time) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all required fields.' })
            return
        }
        try {
            setSaving(true)
            const { error } = await supabase
                .from('branches')
                .update({
                    name: formData.name.trim(),
                    address: formData.address.trim(),
                    open_time: formData.open_time,
                    close_time: formData.close_time,
                    operating_start_date: formData.operating_start_date || null,
                    status: formData.status,
                    branch_owner: formData.branch_owner.trim() || null,
                    manager: formData.owner_is_manager
                        ? formData.branch_owner.trim() || null
                        : formData.manager.trim() || null,
                    tin: formData.tin.trim() || null,
                    facebook_url: formData.facebook_url.trim() || null,
                    phone_number: formData.phone_number.trim() || null,
                    telephone_number: formData.telephone_number.trim() || null,
                    email: formData.email.trim() || null,
                    eod_report_time: formData.eod_report_time || null,
                })
                .eq('branch_id', branchId)

            if (error) throw error

            for (const [category, files] of Object.entries(newDocFilesByCategory)) {
                for (const file of files) {
                    await uploadDocument(file, category)
                }
            }

            await fetchBranch()
            await fetchDocuments()
            setNewDocFilesByCategory({})
            setEditing(false)

            Swal.fire({ icon: 'success', title: 'Branch Updated', timer: 1800, showConfirmButton: false })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Update Branch', text: err.message })
        } finally {
            setSaving(false)
        }
    }

    const uploadDocument = async (file, category) => {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png']
        if (!allowedTypes.includes(file.type)) throw new Error('Only PDF, JPG, JPEG, and PNG files are allowed.')
        if (file.size > 10 * 1024 * 1024) throw new Error('File size must not exceed 10MB.')

        const fileExt = file.name.split('.').pop()
        const fileName = `${branchId}/${category}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`

        const { error: uploadError } = await supabase.storage
            .from('branch_docu')
            .upload(fileName, file, { cacheControl: '3600', upsert: false })
        if (uploadError) throw uploadError

        const { data } = supabase.storage.from('branch_docu').getPublicUrl(fileName)

        const { error: insertErr } = await supabase.from('branch_documents').insert([{
            branch_id: branchId,
            file_name: file.name,
            file_url: data.publicUrl,
            file_type: file.type,
            doc_category: category,
        }])
        if (insertErr) throw insertErr
    }

    const handleDeleteDocument = async (documentId) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Remove Document?',
            showCancelButton: true, confirmButtonText: 'Remove', confirmButtonColor: '#8B5A3C'
        })
        if (!result.isConfirmed) return
        try {
            const { error } = await supabase.from('branch_documents').delete()
                .eq('document_id', documentId)
                .eq('branch_id', branchId) // guard: only this branch's own docs
            if (error) throw error
            setDocuments(prev => prev.filter(d => d.document_id !== documentId))
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Remove Document', text: err.message })
        }
    }

    const cancelEdit = () => {
        setFormData(initialFormData)
        setNewDocFilesByCategory({})
        setEditing(false)
    }

    const formatTime = (v) => {
        if (!v) return '-'
        const [hours, minutes] = v.split(':')
        let h = parseInt(hours, 10)
        const suffix = h >= 12 ? 'PM' : 'AM'
        h = h % 12 || 12
        return `${h}:${minutes} ${suffix}`
    }
    const formatDate = (v) => v
        ? new Date(v).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
        : '-'

    const groupedDocuments = DOCUMENT_CATEGORIES.reduce((acc, cat) => {
        acc[cat.key] = documents.filter(d => d.doc_category === cat.key)
        return acc
    }, {})

    if (!loading && !branch) {
        return (
            <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="p-8">
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                        We couldn't find a branch matching your account. Please contact an administrator.
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">

            <div className="p-8 max-w-4xl mx-auto">
                {loading || !formData ? (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-500">
                        Loading branch details...
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
                            <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">{branch.name}</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">{branch.address}</p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(branch.status)}`}>
                                    {branch.status}
                                </span>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-gray-800">Branch Information</h3>
                                {!editing && (
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="px-4 py-2 bg-[#8B5A3C] text-white rounded-lg text-sm font-medium hover:bg-[#6d4730] transition-colors"
                                    >
                                        Edit Details
                                    </button>
                                )}
                            </div>

                            <div className="px-6 py-6 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name <span className="text-red-500">*</span></label>
                                        <input type="text" placeholder="Enter Branch Name" value={formData.name} disabled={!editing} maxLength={100}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
                                        <textarea rows="2" placeholder="Enter Branch Address" value={formData.address} disabled={!editing} maxLength={255}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Open Time <span className="text-red-500">*</span></label>
                                        <input type="time" value={formData.open_time} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, open_time: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Close Time <span className="text-red-500">*</span></label>
                                        <input type="time" value={formData.close_time} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, close_time: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    {/* <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">EOD Report Time</label>
                                        <input type="time" value={formData.eod_report_time} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, eod_report_time: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div> */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select value={formData.status} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`}>
                                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Operating Since</label>
                                        <input type="date" value={formData.operating_start_date} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, operating_start_date: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Branch Owner</label>
                                        <input type="text" placeholder="Enter Branch Owner" value={formData.branch_owner} disabled={!editing} maxLength={100}
                                            onChange={(e) => setFormData({ ...formData, branch_owner: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                                        <input type="text" placeholder="Enter Manager Name"
                                            value={formData.owner_is_manager ? formData.branch_owner : formData.manager}
                                            disabled={!editing || formData.owner_is_manager}
                                            onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                                            maxLength={100}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${
                                                (!editing || formData.owner_is_manager) ? 'bg-gray-50 text-gray-500' : ''
                                            }`} />
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2 -mt-2">
                                        <input
                                            type="checkbox"
                                            id="admin-owner-is-manager"
                                            checked={formData.owner_is_manager}
                                            disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, owner_is_manager: e.target.checked })}
                                            className="w-4 h-4 accent-[#8B5A3C] cursor-pointer disabled:cursor-not-allowed"
                                        />
                                        <label htmlFor="admin-owner-is-manager" className="text-sm text-gray-600 cursor-pointer">
                                            Branch owner is also the branch manager
                                        </label>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">TIN</label>
                                        <input type="text" placeholder="e.g. 123-456-789-000" value={formData.tin} disabled={!editing} maxLength={20}
                                            onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                        <input type="email" value={formData.email} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Email</label>
                                        <input type="email" value={formData.business_email || 'Not configured'} disabled
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
                                        <p className="text-xs text-gray-400 mt-1">Managed by Super Admin under Branch Management. This is where EOD reports are sent.</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Facebook Page Link</label>
                                        <input type="url" value={formData.facebook_url} disabled={!editing}
                                            onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                        <input type="tel" placeholder="09xx xxx xxxx" value={formData.phone_number} disabled={!editing} maxLength={15}
                                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Telephone Number</label>
                                        <input type="tel" placeholder="(02) xxx xxxx" value={formData.telephone_number} disabled={!editing} maxLength={15}
                                            onChange={(e) => setFormData({ ...formData, telephone_number: e.target.value })}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C] ${!editing ? 'bg-gray-50 text-gray-500' : ''}`} />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Branch Documents</label>
                                    {loadingDocs && <p className="text-sm text-gray-400 mb-2">Loading documents...</p>}
                                    <div className="space-y-4">
                                        {DOCUMENT_CATEGORIES.map((cat) => {
                                            const existingDocs = groupedDocuments[cat.key] || []
                                            const stagedFiles = newDocFilesByCategory[cat.key] || []
                                            return (
                                                <div key={cat.key} className="border border-gray-200 rounded-lg p-3">
                                                    <p className="text-sm font-medium text-gray-700 mb-2">{cat.label}</p>
                                                    {editing && (
                                                        <input
                                                            type="file" multiple
                                                            accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
                                                            onChange={(e) => {
                                                                const files = Array.from(e.target.files || [])
                                                                setNewDocFilesByCategory(prev => ({
                                                                    ...prev,
                                                                    [cat.key]: [...(prev[cat.key] || []), ...files]
                                                                }))
                                                                e.target.value = ''
                                                            }}
                                                            className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]"
                                                        />
                                                    )}
                                                    {existingDocs.length > 0 && (
                                                        <ul className="mt-2 space-y-1">
                                                            {existingDocs.map((doc) => (
                                                                <li key={doc.document_id} className="flex items-center justify-between text-sm bg-gray-50 px-3 py-1.5 rounded">
                                                                    <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-[#8B5A3C] hover:underline truncate">
                                                                        {doc.file_name}
                                                                    </a>
                                                                    {editing && (
                                                                        <button type="button" onClick={() => handleDeleteDocument(doc.document_id)}
                                                                            className="text-red-600 hover:text-red-800 text-xs font-medium ml-2">
                                                                            Remove
                                                                        </button>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {stagedFiles.length > 0 && (
                                                        <ul className="mt-2 space-y-1">
                                                            {stagedFiles.map((file, idx) => (
                                                                <li key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm bg-amber-50 px-3 py-1.5 rounded">
                                                                    <span className="truncate">{file.name}</span>
                                                                    <button type="button"
                                                                        onClick={() => setNewDocFilesByCategory(prev => ({
                                                                            ...prev,
                                                                            [cat.key]: prev[cat.key].filter((_, i) => i !== idx)
                                                                        }))}
                                                                        className="text-red-600 hover:text-red-800 text-xs font-medium ml-2">
                                                                        Remove
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {!editing && existingDocs.length === 0 && (
                                                        <p className="text-xs text-gray-400">No documents uploaded.</p>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {editing && (
                                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                                    <button onClick={cancelEdit} disabled={saving}
                                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50">
                                        Cancel
                                    </button>
                                    <button onClick={handleSave} disabled={!hasChanges || saving}
                                        className={`px-6 py-2 rounded-lg font-medium ${
                                            !hasChanges || saving ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#8B5A3C] text-white hover:bg-[#6d4730]'
                                        }`}>
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-gray-400 mt-4">
                            Since {formatDate(branch.operating_start_date)} · Hours {formatTime(branch.open_time)} – {formatTime(branch.close_time)}
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}